import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, RotateCcw, Trash2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Trash dialog for soft-deleted orders.
 * - Lists orders deleted within the last 30 days (sorted by deletion date)
 * - Each row: order number, site, supplier, total, items, days remaining
 * - Actions: Restore (move back to active orders) or Purge (permanent delete)
 */
export default function OrdersTrashDialog({ open, onClose, onRestored }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actingId, setActingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`${API}/api/orders/trash`);
            setItems(r.data || []);
        } catch (e) {
            toast.error('Impossible de charger la corbeille');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (open) load(); }, [open, load]);

    const restore = async (id, label) => {
        if (actingId) return;
        setActingId(id);
        try {
            const r = await api.post(`${API}/api/orders/${id}/restore`);
            toast.success(`${r.data.order_number} restaurée (${r.data.movements_restored} mouvement(s) de stock réintégré(s))`);
            await load();
            onRestored?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || `Erreur lors de la restauration de ${label}`);
        } finally {
            setActingId(null);
        }
    };

    const purge = async (id, label) => {
        if (actingId) return;
        if (!window.confirm(`Supprimer définitivement ${label} ?\n\nCette action est irréversible.`)) return;
        setActingId(id);
        try {
            await api.delete(`${API}/api/orders/${id}/purge`);
            toast.success(`${label} supprimée définitivement`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la suppression définitive');
        } finally {
            setActingId(null);
        }
    };

    const fmtDate = (iso) => {
        if (!iso) return '?';
        const d = new Date(iso);
        return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-4xl sm:max-w-4xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="w-5 h-5 text-slate-500" />
                        Corbeille des commandes
                    </DialogTitle>
                    <DialogDescription>
                        Commandes supprimées dans les 30 derniers jours. Au-delà, elles sont définitivement effacées au prochain redémarrage du serveur.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-10 text-slate-400">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-10 text-slate-400" data-testid="trash-empty">
                        <Trash2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        La corbeille est vide
                    </div>
                ) : (
                    <div className="overflow-x-auto -mx-1">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 border-b text-[10px] uppercase text-slate-500">
                                <tr>
                                    <th className="px-2 py-1.5 text-left">N°</th>
                                    <th className="px-2 py-1.5 text-left">Site</th>
                                    <th className="px-2 py-1.5 text-left">Fournisseur</th>
                                    <th className="px-2 py-1.5 text-left">Semaine</th>
                                    <th className="px-2 py-1.5 text-right">Total</th>
                                    <th className="px-2 py-1.5 text-center">Articles</th>
                                    <th className="px-2 py-1.5 text-left">Supprimée le</th>
                                    <th className="px-2 py-1.5 text-center">Restant</th>
                                    <th className="px-2 py-1.5 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((o, idx) => {
                                    const urgent = (o.days_remaining ?? 99) <= 5;
                                    return (
                                        <tr
                                            key={o.id}
                                            className={`border-b ${idx % 2 ? 'bg-slate-50' : 'bg-white'}`}
                                            data-testid={`trash-row-${o.order_number}`}
                                        >
                                            <td className="px-2 py-1.5 font-mono font-semibold">{o.order_number}</td>
                                            <td className="px-2 py-1.5">{o.site_name?.replace('FE ', '')}</td>
                                            <td className="px-2 py-1.5">{o.supplier_name}</td>
                                            <td className="px-2 py-1.5 font-mono text-slate-500">{o.week}</td>
                                            <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{o.total_amount?.toFixed(2)} €</td>
                                            <td className="px-2 py-1.5 text-center">{o.items_count}</td>
                                            <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(o.deleted_at)}</td>
                                            <td className="px-2 py-1.5 text-center">
                                                <Badge
                                                    className={urgent
                                                        ? 'bg-red-100 text-red-700'
                                                        : (o.days_remaining ?? 0) <= 10 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}
                                                >
                                                    {urgent && <AlertTriangle className="w-3 h-3 mr-1 inline" />}
                                                    {o.days_remaining} j
                                                </Badge>
                                            </td>
                                            <td className="px-2 py-1.5 text-right whitespace-nowrap">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-6 px-2 text-[11px] mr-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                                    onClick={() => restore(o.id, o.order_number)}
                                                    disabled={actingId === o.id}
                                                    data-testid={`restore-${o.order_number}`}
                                                >
                                                    {actingId === o.id
                                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                                        : <><RotateCcw className="w-3 h-3 mr-1" />Restaurer</>}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-6 px-2 text-[11px] text-red-500 hover:bg-red-50 hover:text-red-700"
                                                    onClick={() => purge(o.id, o.order_number)}
                                                    disabled={actingId === o.id}
                                                    data-testid={`purge-${o.order_number}`}
                                                    title="Supprimer définitivement"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
